/**
 * CreateTaxonomyModal
 *
 * Modal for creating a brand-new taxonomy from scratch.
 * Accepts a taxonomy key, display name, and JSON payload.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../ui/dialog';
import { Button } from '../../../../ui/button';
import { Textarea } from '../../../../ui/textarea';
import { Label } from '../../../../ui/label';
import { Input } from '../../../../ui/input';
import { useCreateDraft } from '../../hooks/useTaxonomy';

interface CreateTaxonomyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CreateTaxonomyModal: React.FC<CreateTaxonomyModalProps> = ({
  open,
  onOpenChange,
}) => {
  const [taxonomyKey, setTaxonomyKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [versionNumber, setVersionNumber] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const createDraft = useCreateDraft();

  const handleSubmit = () => {
    setParseError(null);
    if (!taxonomyKey.trim()) {
      setParseError('Taxonomy key is required.');
      return;
    }
    try {
      const parsed = JSON.parse(jsonInput);
      if (!parsed.dimensions || !Array.isArray(parsed.dimensions)) {
        setParseError('JSON must contain a "dimensions" array.');
        return;
      }
      createDraft.mutate(
        {
          taxonomyKey: taxonomyKey.trim(),
          options: {
            createNew: true,
            taxonomyData: parsed,
            displayName: displayName.trim() || undefined,
            ...(versionNumber ? { versionNumber } : {}),
          },
        },
        {
          onSuccess: () => {
            resetForm();
            onOpenChange(false);
          },
        }
      );
    } catch {
      setParseError('Invalid JSON. Please check your input.');
    }
  };

  const resetForm = () => {
    setTaxonomyKey('');
    setDisplayName('');
    setJsonInput('');
    setVersionNumber('');
    setParseError(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Taxonomy</DialogTitle>
          <DialogDescription>
            Define a new taxonomy with its initial data. The taxonomy key is a
            unique identifier used to link prompts to this taxonomy.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="taxonomy-key">Taxonomy Key <span className="text-red-500">*</span></Label>
            <Input
              id="taxonomy-key"
              placeholder="e.g. banking, wealth-management"
              value={taxonomyKey}
              onChange={(e) => setTaxonomyKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="mt-1 font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">Lowercase kebab-case. Links prompts to this taxonomy.</p>
          </div>

          <div>
            <Label htmlFor="display-name">Display Name</Label>
            <Input
              id="display-name"
              placeholder="e.g. Banking Taxonomy"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Human-readable name. Auto-generated from key if empty.</p>
          </div>

          <div>
            <Label htmlFor="version-number">Version Number (optional)</Label>
            <Input
              id="version-number"
              placeholder="e.g. 1.0"
              value={versionNumber}
              onChange={(e) => setVersionNumber(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="taxonomy-json">Taxonomy JSON <span className="text-red-500">*</span></Label>
            <Textarea
              id="taxonomy-json"
              placeholder='{"dimensions": [...]}'
              value={jsonInput}
              onChange={(e) => { setJsonInput(e.target.value); setParseError(null); }}
              className="mt-1 font-mono text-sm"
              rows={12}
            />
            {parseError && (
              <p className="text-sm text-red-600 mt-1">{parseError}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={createDraft.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!taxonomyKey.trim() || !jsonInput.trim() || createDraft.isPending}
          >
            {createDraft.isPending ? 'Creating...' : 'Create Taxonomy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaxonomyModal;
